import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import {
  Code2, Sun, Moon, LogOut, User, LayoutDashboard, Bell
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '../components/ui/popover';
import LlmStatusPill from '../components/admin/LlmStatusPill';
import { notificationsApi } from '../lib/api';

import { AdminSidebar } from './admin/AdminSidebar';
import { AdminOverview } from './admin/AdminOverview';
import { SchoolsManagement } from './admin/SchoolsManagement';
import { UsersManagement } from './admin/UsersManagement';
import { CoursesManagement } from './admin/CoursesManagement';
import { RequestsManagement } from './admin/RequestsManagement';
import { GrowthAnalytics } from './admin/GrowthAnalytics';
import { ExamDashboard } from './admin/ExamDashboard';
import { SystemPanel } from './admin/SystemPanel';
import { FeatureFlags } from './admin/FeatureFlags';
import { QuestionBankAdmin } from './admin/QuestionBankAdmin';
import { ChapterWeightageAdmin } from './admin/ChapterWeightageAdmin';
import { LabAnalytics } from './admin/LabAnalytics';
import { GradeChangeRequests } from './admin/GradeChangeRequests';
import { GradeAllocation } from './admin/GradeAllocation';
import { CourseAuthoring } from './admin/CourseAuthoring';
import { CourseAuthoringDetail } from './admin/CourseAuthoringDetail';
import { HealthCheck } from './admin/HealthCheck';
import { TopicRequestsAdmin } from './admin/TopicRequestsAdmin';
import { AvatarVoicesAdmin } from './admin/AvatarVoicesAdmin';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await notificationsApi.get();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNotificationClick = (notification) => {
    if (notification.type === 'course_request') {
      navigate('/admin/requests');
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      <AdminSidebar user={user} />

      <div className="flex-1 flex flex-col">
        <header className="glass border-b border-border/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </Link>

            <div className="flex items-center gap-3">
              <LlmStatusPill />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="admin-notifications-btn">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0">
                  <div className="p-3 border-b border-border">
                    <h4 className="font-semibold">Notifications</h4>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground text-sm">No new notifications</div>
                    ) : (
                      notifications.map((notification) => (
                        <div key={notification.id}
                          className="p-3 border-b border-border hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => handleNotificationClick(notification)}>
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Bell className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium">{notification.title}</p>
                              <p className="text-xs text-muted-foreground truncate">{notification.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">{new Date(notification.timestamp).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="p-2 border-t border-border">
                      <Button variant="ghost" className="w-full text-sm" onClick={() => navigate('/admin/requests')}>
                        View All Requests
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>

              <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <LayoutDashboard className="w-4 h-4 mr-2" />Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="growth" element={<GrowthAnalytics />} />
            <Route path="exam-dashboard" element={<ExamDashboard />} />
            <Route path="schools" element={<SchoolsManagement />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="courses" element={<CoursesManagement />} />
            <Route path="grade-allocation" element={<GradeAllocation />} />
            <Route path="grade-requests" element={<GradeChangeRequests />} />
            <Route path="lab-analytics" element={<LabAnalytics />} />
            <Route path="course-authoring" element={<CourseAuthoring />} />
            <Route path="course-authoring/:courseId" element={<CourseAuthoringDetail />} />
            <Route path="feature-flags" element={<FeatureFlags />} />
            <Route path="question-bank" element={<QuestionBankAdmin />} />
            <Route path="chapter-weightage" element={<ChapterWeightageAdmin />} />
            <Route path="requests" element={<RequestsManagement />} />
            <Route path="topic-requests" element={<TopicRequestsAdmin />} />
            <Route path="avatar-voices" element={<AvatarVoicesAdmin />} />
            <Route path="system" element={<SystemPanel />} />
            <Route path="health-check" element={<HealthCheck />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
